import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "app/services/auth-services";
import { todayDate } from "app/shared/ReuseComponents/DateFormatter";
import { generateHRApprovalMail } from "app/utils/documentUtils";
import { FirstLetterCaps } from "app/shared/ReuseComponents/StyledComponents";

export const useApprovalSubmit = (
  params,
  requests,
  approvers,
  approvalData,
  currentIndex,
) => {
  const safeParseId = (val) => {
    if (val === null || val === undefined) return null;
    if (typeof val === "object") {
      return val.id ? parseInt(val.id) : null;
    }
    const parsed = parseInt(val);
    return isNaN(parsed) ? null : parsed;
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const sendNotification = useCallback((payload) => {
    axios
      .post(`${BASE_URL}/Felix/Notificationscrudwithoutpagination/`, payload)
      .then((res) => {
        console.log("Notification sent:", res);
      })
      .catch((error) => {
        console.log("Error sending notification:", error);
      });
  }, []);

  const sendEmail = useCallback(async (payload) => {
    try {
      await axios.post(`${BASE_URL}/Felix/Approvalrejectmail/`, payload);
    } catch (error) {
      console.log("Error sending email:", error);
    }
  }, []);

  const getWorkflowDetails = useCallback(() => {
    const { WorkflowName } = params;

    const details = {
      idField: "",
      requestField: "",
      updateEndpoint: "",
      approvalField: "",
      rejectionField: "",
      mailSubject: "",
    };

    switch (WorkflowName) {
      case "Leave":
        details.idField = "Application_Id";
        details.requestField = "Application_Id";
        details.updateEndpoint = "/Felix/updateleave/";
        details.approvalField = "Leave_flg";
        details.rejectionField = "Leave_flg";
        details.mailSubject = "Re: Leave Approval";
        break;

      case "Leave Cancellation":
        details.idField = "Cancellation_Id";
        details.requestField = "Cancellation_Id";
        details.updateEndpoint = "/Felix/ApproveLeaveCancellation/";
        details.approvalField = "Leave_flg";
        details.rejectionField = "Leave_flg";
        details.mailSubject = "Re: Leave Cancel Approval";
        break;

      case "timesheet":
        details.idField = "TimeSheet_Id";
        details.requestField = "TimeSheet_Id";
        details.updateEndpoint = "/Felix/TimeSheetwithoutCRUD/";
        details.approvalField = "Timesheet_flg";
        details.rejectionField = "Timesheet_flg";
        details.mailSubject = "Re: Timesheet Approval";
        break;

      case "TimesheetLog":
        details.idField = "TimeSheet_Id";
        details.requestField = "TimeSheet_Id";
        details.updateEndpoint = "/Felix/TimeSheetwithoutCRUD/";
        details.approvalField = "Timesheet_flg";
        details.rejectionField = "Timesheet_flg";
        details.mailSubject = "Re: Timesheet Approval";
        break;

      case "Reimbursement":
        details.idField = "Employeeclaim_Id";
        details.requestField = "Employeeclaim_Id";
        details.updateEndpoint = "/Felix/EmployeeclaimsCRUD/";
        details.approvalField = "Claim_Status";
        details.rejectionField = "Claim_Status";
        details.mailSubject = "Re: Claim Approval";
        break;

      case "Full and Final Settlement":
        details.idField = "FullandFinal_Id";
        details.requestField = "FullandFinal_Id";
        details.updateEndpoint = "/Felix/FullandFinalCRUD/";
        details.approvalField = "Approved_Flg";
        details.rejectionField = "Approved_Flg";
        details.mailSubject = "Re: Full and Final Settlement Approval";
        break;

      case "Resignation":
        details.idField = "EmployeeResignation_Id";
        details.requestField = "EmployeeResignation_Id";
        details.updateEndpoint = "/Felix/EmployeeResignationCRUD/";
        details.approvalField = "Resignation_flg";
        details.rejectionField = "Resignation_flg";
        details.mailSubject = "Re: Resignation Approval";
        break;

      case "Attandance Management":
        details.idField = "Employee_Id";
        details.updateEndpoint = "/Felix/Attandancemasterflagchange/";
        details.mailSubject = "Re: Attendance Approval";
        break;

      case "payroll management":
        details.idField = "Payslip_Id";
        details.updateEndpoint = "/Felix/Payslipheaderflagchange/";
        details.mailSubject = "Re: Payroll Approval";
        break;

      default:
        break;
    }

    return details;
  }, [params]);

  const getEmailContent = useCallback(
    (isApproved, initiator, isFinal) => {
      return getCleanApprovalMail({ isApproved, initiator, isFinal });
    },
    [params, approvers, currentIndex],
  );
  const handleBulkApproval = useCallback(
    async (workflowDetails, isApproved) => {
      const { WorkflowName } = params;

      if (WorkflowName === "Attandance Management") {
        const payload = {
          Attandancemasterid: JSON.parse(params.Id.replace(/'/g, '"')),
          Approved_Flag: isApproved,
          Status: isApproved,
          Filter_status: isApproved ? "Approved" : "Rejected",
          Updated_By: requests?.[0]?.Updated_By?.id,
        };

        return await axios.put(
          `${BASE_URL}/Felix/Attandancemasterflagchange/`,
          payload,
        );
      }

      if (WorkflowName === "payroll management") {
        const payload = {
          Payslipheaderid: JSON.parse(params.Id.replace(/'/g, '"')),
          Approved_Flag: isApproved,
          Status: isApproved,
          Updated_By: requests?.[0]?.Updated_By?.id,
        };

        return await axios.put(
          `${BASE_URL}/Felix/Payslipheaderflagchange/`,
          payload,
        );
      }

      if (WorkflowName === "TimesheetLog") {
        const payload = {
          TimeSheet_Id: JSON.parse(params.Id.replace(/'/g, '"')),
          Approved_Flag: isApproved,
          Status: isApproved,
          Timesheet_flg: isApproved ? "Approved" : "Rejected",
          Updated_By: requests?.[0]?.Created_By?.id,
        };

        return await axios.put(
          `${BASE_URL}/Felix/Timesheetflagchange/`,
          payload,
        );
      }

      return null;
    },
    [params, requests],
  );

  const handleSingleApproval = useCallback(
    async (workflowDetails, isApproved) => {
      const { WorkflowName } = params;
      const currentApproval = approvalData[currentIndex];

      let payload = { ...requests };

      // Common fields
      payload.Approved_Flag = isApproved;
      payload.Leavecancel_flg = isApproved;
      payload.Status = true;

      if (isApproved) {
        payload[workflowDetails.approvalField] = "Approved";
      } else {
        payload[workflowDetails.rejectionField] = "Rejected";
      }

      // Add specific fields based on workflow
      switch (WorkflowName) {
        case "Leave":
          payload.Sanctioned_Date = todayDate;
          payload.Sancleavefrom_Date = todayDate;
          payload.Sancleaveto_Date = todayDate;
          payload.Leavetype_Id = safeParseId(requests?.Leavetype_Id);
          payload.Employee_Id = safeParseId(requests?.Employee_Id);
          payload.Approved_By = safeParseId(requests?.Updated_By) || safeParseId(requests?.Created_By);
          break;

        case "Leave Cancellation":
          payload.Leave_Id = safeParseId(requests?.Application_Id);
          payload.LeaveCancel_Id = safeParseId(requests?.id);
          payload.Application_Id = safeParseId(requests?.Application_Id);
          payload.Leavetype_Id = safeParseId(requests?.Leavetype_Id);
          break;

        case "timesheet":
          payload.Employee_Id = safeParseId(requests?.Employee_Id);
          break;

        case "Reimbursement":
          payload.Claim_Date = requests?.Claim_Date || todayDate;
          payload.Reimbursement_Id = requests?.Reimbursement_Id?.id;
          // Remove attachment field if present
          delete payload.Claim_Attachment;
          break;

        case "Full and Final Settlement":
          // No additional fields needed
          break;

        case "Resignation":
          payload.Resign_Date = todayDate;
          payload.Relieving_Date = todayDate;
          payload.Employee_Id = safeParseId(requests?.Employee_Id);
          payload.Approved_By = safeParseId(requests?.Updated_By);
          break;
      }

      // Add company/partner IDs
      payload.Company_Id = safeParseId(requests?.Company_Id);
      payload.Partner_Id = safeParseId(requests?.Partner_Id);
      payload.Created_By = safeParseId(requests?.Created_By);
      payload.Updated_By = isApproved
        ? (safeParseId(requests?.Updated_By) || safeParseId(requests?.Created_By))
        : safeParseId(requests?.Created_By);

      return await axios.put(
        `${BASE_URL}${workflowDetails.updateEndpoint}`,
        payload,
      );
    },
    [params, requests, approvalData, currentIndex],
  );

  const getCleanApprovalMail = ({ isApproved, initiator, isFinal }) => {
    const { WorkflowName } = params;

    const currentApprover = approvers[currentIndex];
    
    const firstName = initiator?.Employee_FirstName || initiator?.first_name || "";
    const lastName = initiator?.Employee_LastName || initiator?.Employee_LasttName || initiator?.last_name || "";
    const employeeName = `${firstName} ${lastName}`.trim() || "Employee";

    const approverName = currentApprover?.Approvername || "Approver";
    const statusColor = isApproved ? "#16a34a" : "#dc2626";
    const approverComments = currentApprover?.comments || "";

    const statusText = isApproved
      ? (isFinal ? "Approved" : `Approved by ${approverName || currentApprover?.ApproverRole}`)
      : "Rejected";

    const approvalMessage = isApproved
      ? (isFinal
          ? `Your request for <strong>${WorkflowName}</strong> has been <span style="color: ${statusColor}; font-weight: bold;">Approved</span>.`
          : `Your request for <strong>${WorkflowName}</strong> has been <span style="color: ${statusColor}; font-weight: bold;">Approved by ${approverName || currentApprover?.ApproverRole}</span>.`
        )
      : `Your request for <strong>${WorkflowName}</strong> has been <span style="color: ${statusColor}; font-weight: bold;">Rejected</span>.`;

    return `
    <div style="font-family: Arial, sans-serif;">
      <div>
        <p style="font-size: 14px; color: #374151;">
          Hello <strong>${employeeName}</strong>,
        </p>

        <p style="font-size: 14px; color: #374151;">
          ${approvalMessage}
        </p>

        <div style="margin-top: 20px; padding: 15px; background: #f9fafb; border-radius: 6px;">
          <p style="margin: 6px 0; font-size: 13px;">
            <strong>Request Type:</strong> ${WorkflowName}
          </p>

          <p style="margin: 6px 0; font-size: 13px;">
            <strong>Processed By:</strong> ${approverName}
          </p>

          <p style="margin: 6px 0; font-size: 13px;">
            <strong>Status:</strong> 
            <span style="color: ${statusColor}; font-weight: bold;">
              ${statusText}
            </span>
          </p>

          <p style="margin: 6px 0; font-size: 13px;">
            <strong>Date:</strong> ${new Date().toLocaleString()}
          </p>
        </div>

        ${
          approverComments
            ? `
          <div style="margin-top: 15px; padding: 12px; background: ${isApproved ? "#f0fdf4" : "#fef2f2"}; border-left: 4px solid ${statusColor};">
            <p style="margin: 0; font-size: 13px; color: ${isApproved ? "#166534" : "#991b1b"};">
              <strong>Approver Comments:</strong> ${approverComments}
            </p>
          </div>
        `
            : ""
        }

        ${
          !isApproved
            ? `
          <div style="margin-top: 15px; padding: 12px; background: #fef2f2; border-radius: 6px;">
            <p style="margin: 0; font-size: 13px; color: #991b1b;">
              Please contact the HR department for further clarification.
            </p>
          </div>
        `
            : ""
        }

        <p style="margin-top: 25px; font-size: 13px; color: #6b7280;">
          This is an automated message from HR Management System.
        </p>

        <p style="margin-top: 10px; font-size: 13px;">
          Regards,<br/>
          HR Team
        </p>
      </div>
    </div>
    `;
  };

  const sendToNextApprover = useCallback(
    async (documentApprovalId) => {
      const { WorkflowName } = params;
      const nextApproverIndex = currentIndex + 1;

      if (nextApproverIndex >= approvers.length) {
        return; // No more approvers
      }

      const mailContent = getMailContentForNextApprover();

      const payload = {
        mailhtml: mailContent,
        Created_By: requests?.Created_By?.id || requests?.[0]?.Created_By?.id,
        mailsubject: getMailSubject(WorkflowName),
        approver_name: approvers[nextApproverIndex]?.Approvername,
        Approverid: approvers[nextApproverIndex]?.id,
        PreviousApproverid: approvers[currentIndex]?.id,
        Document_Type: WorkflowName,
        id: params.Id,
        Comments: String(nextApproverIndex + 1),
        documentapprovalid: documentApprovalId,
        index: nextApproverIndex + 1,
        Initiated_By: getInitiatorName(),
      };

      await axios.post(`${BASE_URL}/Felix/HRApprovalsystem/`, payload);

      // SEND PUSH NOTIFICATION FOR NEXT APPROVER (LEAVE, TIMESHEET, & LEAVE CANCELLATION)
      if (
        WorkflowName === "Leave" ||
        WorkflowName === "timesheet" ||
        WorkflowName === "Leave Cancellation"
      ) {
        try {
          await axios.post(`${BASE_URL}/SendPushNotificationView/`, {
            User_Id: payload.Approverid,
            Title:
              WorkflowName === "Leave"
                ? "Leave Approval Request"
                : WorkflowName === "timesheet"
                  ? "Timesheet Approval Request"
                  : "Leave Cancellation Approval Request",
            Body:
              WorkflowName === "Leave"
                ? `You have a new leave approval request from ${getInitiatorName()}`
                : WorkflowName === "timesheet"
                  ? `You have a new timesheet approval request from ${getInitiatorName()}`
                  : `You have a new leave cancellation approval request from ${getInitiatorName()}`,
          });
          console.log("Push notification sent for next approver in hook");
        } catch (err) {
          console.log("Push notification error in hook", err);
        }
      }
    },
    [params, approvers, currentIndex, requests],
  );

  const getMailContentForNextApprover = useCallback(() => {
    const { WorkflowName } = params;
    const currentApprover = approvers[currentIndex];
    const nextApprover = approvers[currentIndex + 1];

    const requesterObj = (requests?.Employee_Id && typeof requests.Employee_Id === "object")
      ? requests.Employee_Id
      : (requests?.[0]?.Employee_Id && typeof requests?.[0]?.Employee_Id === "object")
        ? requests[0].Employee_Id
        : requests?.Created_By || requests?.[0]?.Created_By;

    return `
      <div style="width: 100%; font-family: Arial, sans-serif;">
        Dear ${nextApprover?.Approvername},<br><br>
        ${requesterObj?.Employee_FirstName || requesterObj?.first_name || "Employee"} has submitted a 
        <b>${WorkflowName}</b> for your approval.<br>
        To review and respond, <a href='{{url}}'>click here</a>.<br><br>
        Thanks and Regards,<br>
        ${currentApprover?.Approvername}<br>
        ${currentApprover?.ApproverRole}<br>
      </div>
    `;
  }, [params, approvers, currentIndex, requests]);

  const getMailSubject = (workflowName) => {
    const subjects = {
      Leave: "Leave Approval Request",
      "Leave Cancellation": "Leave Cancellation Approval Request",
      timesheet: "Timesheet Approval Request",
      TimesheetLog: "Timesheet Approval Request",
      "Attandance Management": "Attendance Report For Approval",
      "payroll management": "Payroll Approval Request",
      "Full and Final Settlement": "Full and Final Settlement Request",
      Reimbursement: "Claim Approval Request",
      Resignation: "Resignation Approval Request",
    };

    return subjects[workflowName] || `${workflowName} Approval Request`;
  };

  const getInitiatorName = () => {
    const requester = (requests?.Employee_Id && typeof requests.Employee_Id === "object")
      ? requests.Employee_Id
      : (requests?.[0]?.Employee_Id && typeof requests?.[0]?.Employee_Id === "object")
        ? requests[0].Employee_Id
        : requests?.Created_By || requests?.[0]?.Created_By;

    return `${requester?.Employee_FirstName || requester?.first_name || ""} ${requester?.Employee_LastName || requester?.Employee_LasttName || requester?.last_name || ""}`.trim() || "Employee";
  };

  const handleSubmit = useCallback(
    async (formData) => {
      setIsSubmitting(true);

      try {
        const { WorkflowName } = params;
        const workflowDetails = getWorkflowDetails();
        const isApproved = formData.status_btn === "yes";
        const currentApproval = approvalData[currentIndex];

        // Mark as approved in local state
        currentApproval.OneTimeApproved_flg = true;

        // Update document approval record
        const approvalPayload = {
          Documentapproval_Id: params.documentapprovalid,
          Request_Id:
            requests?.[workflowDetails.requestField] ||
            requests?.[0]?.[workflowDetails.requestField],
          Created_By: requests?.Created_By?.id || requests?.[0]?.Created_By?.id,
          Updated_By: requests?.Updated_By?.id || requests?.[0]?.Updated_By?.id,
          Approval_Id: approvalData,
          Approve: approvalData,
          Request_Key: requests?.id,
          Request_Name: WorkflowName,
        };

        const approvalResponse = await axios.put(
          `${BASE_URL}/Felix/DocumentapprovalCRUD/`,
          approvalPayload,
        );

        if (approvalResponse.data.id) {
          toast.success(
            `Document ${isApproved ? "Approved" : "Rejected"} Successfully`,
          );

          // Handle bulk approvals (Attendance, Payroll)
          if (
            WorkflowName === "Attandance Management" ||
            WorkflowName === "payroll management" ||
            WorkflowName === "TimesheetLog"
          ) {
            await handleBulkApproval(workflowDetails, isApproved);
          } else {
            // Handle single document approvals
            await handleSingleApproval(workflowDetails, isApproved);
          }

          const initiatorEmail =
            (requests?.Employee_Id && typeof requests.Employee_Id === "object" && (requests.Employee_Id.Email_Id || requests.Employee_Id.email)) ||
            (approvalResponse?.data?.Employee_Id && typeof approvalResponse.data.Employee_Id === "object" && (approvalResponse.data.Employee_Id.Email_Id || approvalResponse.data.Employee_Id.email)) ||
            approvalResponse?.data?.Created_By?.email ||
            approvalResponse?.data?.Created_By?.Email_Id ||
            requests?.Created_By?.email ||
            requests?.Created_By?.Email_Id ||
            "";

          if (initiatorEmail) {
            const initiatorObj = (requests?.Employee_Id && typeof requests.Employee_Id === "object")
              ? requests.Employee_Id
              : (approvalResponse?.data?.Employee_Id && typeof approvalResponse.data.Employee_Id === "object")
                ? approvalResponse.data.Employee_Id
                : approvalResponse?.data?.Created_By || requests?.Created_By || requests?.Employee_Id;

            await sendEmail({
              MailId: initiatorEmail,
              ApproverId: approvers[currentIndex]?.id,
              mailsubject: workflowDetails?.mailSubject || `${FirstLetterCaps(WorkflowName)} - Approval Status`,
              mailhtml: getEmailContent(
                isApproved,
                initiatorObj,
                !isApproved || (currentIndex === approvers.length - 1)
              ),
            });
          }

          // Send notification
          const notificationPayload = {
            Notification_Msg: `${WorkflowName} request ${isApproved ? "approved" : "rejected"} by ${approvers[currentIndex]?.Approvername}`,
            Open_flg: false,
            User_Id: parseInt(approvers[currentIndex]?.id),
            Created_By: parseInt(
              requests?.Created_By?.id || requests?.[0]?.Created_By?.id,
            ),
            Partner_Id: parseInt(
              requests?.Partner_Id?.id || requests?.[0]?.Partner_Id?.id,
            ),
            Company_Id: parseInt(
              requests?.Company_Id?.id || requests?.[0]?.Company_Id?.id,
            ),
          };

          sendNotification(notificationPayload);

          // Send to next approver if approved and not last approver
          if (isApproved && currentIndex < approvers.length - 1) {
            await sendToNextApprover(approvalResponse.data.Documentapproval_Id);
            toast.info("Sent to next approver");
          }

          // Close window after delay
          setTimeout(() => {
            window.close();
          }, 2000);
        }
      } catch (error) {
        console.error("Error submitting approval:", error);
        toast.error(`Error: ${error.message}`);

        // Reset approval flag on error
        const updatedApproval = { ...approvalData[currentIndex] };
        updatedApproval.OneTimeApproved_flg = false;
        approvalData[currentIndex] = updatedApproval;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      params,
      requests,
      approvers,
      approvalData,
      currentIndex,
      getWorkflowDetails,
      handleBulkApproval,
      handleSingleApproval,
      getEmailContent,
      sendEmail,
      sendNotification,
      sendToNextApprover,
    ],
  );

  return {
    handleSubmit,
    isSubmitting,
  };
};
